import React from 'react';
import { useWeight } from '../../../context/WeightContext';
import { useAuth } from '../../../context/AuthContext';
import { sb } from '../../../modules/state';
import { lbsToKg, kgToLbs } from '../../../utils/weight';

export function WeightWeighIn() {
  const [kg, setKg] = React.useState<number>(0);
  const [decimal, setDecimal] = React.useState<number>(0);
  const [date, setDate] = React.useState<string>(new Date().toISOString().slice(0, 10));
  const [showAgeError, setShowAgeError] = React.useState(false);
  const [ageError, setAgeError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const { addEntry, unit } = useWeight();
  const { currentUser } = useAuth();
  const [profile, setProfile] = React.useState<{ birthdate?: string; preferred_weight_unit?: string | null; preferred_measure_unit?: string | null } | null>(null);

  // Load profile on mount
  React.useEffect(() => {
    if (currentUser?.id) {
      sb.from('profiles').select('birthdate, preferred_weight_unit, preferred_measure_unit').eq('id', currentUser.id).single().then((response) => {
        if (response.data) setProfile(response.data);
      }).catch(() => {});
    }
  }, [currentUser]);

  // Calculate weight in kg for storage
  // Minimum weight must be > 0 (at least 1 kg or ~2.2 lbs)
  const weightInKg = React.useMemo(() => {
    const weightInPreferredUnit = kg + (decimal / 10);
    if (weightInPreferredUnit <= 0) return 0;
    if (unit === 'lbs') {
      return lbsToKg(weightInPreferredUnit);
    }
    return weightInPreferredUnit;
  }, [kg, decimal, unit]);

  // Calculate age from birthdate
  const calculateAge = (birthdate: string | null) => {
    if (!birthdate) return null;
    const birth = new Date(birthdate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = profile?.birthdate ? calculateAge(profile.birthdate) : null;

  // Default to kg if unit preference not set
  const displayUnit = profile?.preferred_weight_unit || 'kg';

  const handleSave = async () => {
    // Auto-save unit preferences if not set
    if (!profile?.preferred_weight_unit || !profile?.preferred_measure_unit) {
      if (currentUser?.id) {
        try {
          await sb.from('profiles').upsert({
            id: currentUser.id,
            preferred_weight_unit: 'kg',
            preferred_measure_unit: 'cm'
          });
        } catch (e) {
          console.error('Error saving unit preferences:', e);
        }
      }
    }

    if (weightInKg <= 0) return;
    setSaving(true);

    // Age gate check
    if (age !== null) {
      if (age < 18) {
        setAgeError('You must be at least 18 years old to use this app.');
        setShowAgeError(true);
        return;
      }
      if (age > 80) {
        setAgeError('For safety reasons, this app is not recommended for users over 80.');
        setShowAgeError(true);
        return;
      }
    }

    await addEntry(weightInKg, new Date(date).toISOString());
    setKg(0);
    setDecimal(0);
    setShowAgeError(false);
    setAgeError('');
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-2 pb-3">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-sm text-primary">calendar_today</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-transparent border-none text-sm font-medium text-on-surface focus:ring-0 p-0 cursor-pointer"
          />
        </div>
      </div>

      <div className="px-2">
        <div className="border border-outline rounded-lg p-3">
          <div className="flex items-center justify-center gap-2">
            <div className="group relative">
              <input
                className="w-24 bg-surface border-b border-outline text-center py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                inputMode="numeric"
                maxLength={3}
                type="text"
                value={kg > 0 ? kg : ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= 0 && val <= 300) {
                    setKg(val);
                  }
                }}
              />
              <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] text-on-surface-variant opacity-0 group-focus-within:opacity-100 transition-opacity">Whole {displayUnit === 'kg' ? 'kg' : 'lbs'}</span>
            </div>
            <span className="text-xl">.</span>
            <div className="group relative">
              <input
                className="w-12 bg-surface border-b border-outline text-center py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                inputMode="numeric"
                maxLength={1}
                type="text"
                value={decimal > 0 ? decimal : ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= 0 && val <= 9) {
                    setDecimal(val);
                  }
                }}
              />
              <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] text-on-surface-variant opacity-0 group-focus-within:opacity-100 transition-opacity">Decimal</span>
            </div>
            <span className="text-lg text-on-surface-variant ml-2">{displayUnit === 'kg' ? 'kg' : 'lbs'}</span>
          </div>

          <div className="flex items-center justify-center gap-3 mt-4">
            <button
              onClick={() => {
                if (decimal > 0) {
                  setDecimal(d => Math.max(0, d - 1));
                } else if (kg > 0) {
                  setKg(k => Math.max(0, k - 1));
                  setDecimal(9);
                }
              }}
              className="w-8 h-8 flex items-center justify-center border border-outline rounded-md hover:bg-on-surface/5 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <span className="material-symbols-outlined text-lg">remove</span>
            </button>
            <button
              onClick={() => {
                if (decimal < 9) {
                  setDecimal(d => Math.min(9, d + 1));
                } else {
                  setKg(k => Math.min(300, k + 1));
                  setDecimal(0);
                }
              }}
              className="w-8 h-8 flex items-center justify-center border border-outline rounded-md hover:bg-on-surface/5 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <span className="material-symbols-outlined text-lg">add</span>
            </button>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-surface border-t border-outline">
        {showAgeError ? (
          <div className="mb-2 p-3 rounded-lg bg-error-container">
            <p className="text-xs font-medium text-error">{ageError}</p>
          </div>
        ) : null}
        <button
          className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-primary-contrast active:scale-95 transition-transform"
          onClick={handleSave}
          disabled={weightInKg <= 0 || saving}
        >
          Save Weight
        </button>
      </div>
    </div>
  );
}
